import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAdmin } from "../../../utils/auth";
import {
  AccessRequest,
  AccessStatus,
  decideRequest,
  listRequests,
  resendCode,
} from "../../../components/Admin/accessApi";
import styles from "./AccessRequests.module.css";

const TABS: { status: AccessStatus; label: string }[] = [
  { status: "pending", label: "Pending" },
  { status: "approved", label: "Approved" },
  { status: "rejected", label: "Rejected" },
];

function formatDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function codeState(r: AccessRequest): { label: string; tone: "success" | "warning" | "neutral" | "error" } {
  if (r.codeUsedAt) return { label: `Joined ${formatDate(r.codeUsedAt)}`, tone: "success" };
  if (!r.codeExpiresAt) return { label: "No code sent", tone: "error" };
  if (new Date(r.codeExpiresAt).getTime() < Date.now()) return { label: "Code expired", tone: "warning" };
  return { label: `Code valid until ${formatDate(r.codeExpiresAt)}`, tone: "neutral" };
}

/** Admin page: review requests to join mRehab, approve (emails an access code) or reject. */
export default function AccessRequests() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<AccessStatus>("pending");
  const [rows, setRows] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (!isAdmin()) navigate("/login", { replace: true });
  }, [navigate]);

  const load = useCallback(async (status: AccessStatus) => {
    setLoading(true);
    try {
      setRows(await listRequests(status));
    } catch (e) {
      setNotice({ kind: "error", text: (e as Error).message });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  async function act(email: string, action: () => Promise<string>) {
    setBusyEmail(email);
    setNotice(null);
    try {
      setNotice({ kind: "success", text: await action() });
      await load(tab);
    } catch (e) {
      setNotice({ kind: "error", text: (e as Error).message });
    } finally {
      setBusyEmail(null);
    }
  }

  const approve = (email: string) =>
    act(email, async () => {
      const r = await decideRequest(email, "approved");
      return r.codeSent === false
        ? `Approved ${email}, but the code email failed. Use "Resend code" from the Approved tab.`
        : `Approved ${email}. Their access code is on its way.`;
    });

  const reject = (email: string) => {
    if (!window.confirm(`Reject the request from ${email}?`)) return;
    act(email, async () => {
      await decideRequest(email, "rejected");
      return `Rejected ${email}.`;
    });
  };

  const resend = (email: string) =>
    act(email, async () => {
      const r = await resendCode(email);
      return `New code sent to ${email} (valid until ${formatDate(r.codeExpiresAt)}). The old code no longer works.`;
    });

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>Access requests</h1>
          <p className={styles.subtitle}>
            People who asked to join mRehab. Approving emails them a single-use access code that only works with
            their email address.
          </p>
        </div>
        <div className="ui-segmented" role="tablist" aria-label="Request status">
          {TABS.map((t) => (
            <button
              key={t.status}
              type="button"
              role="tab"
              aria-selected={tab === t.status}
              onClick={() => {
                setNotice(null);
                setTab(t.status);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {notice && (
        <p className={`ui-banner ui-banner--${notice.kind}`} role={notice.kind === "error" ? "alert" : "status"}>
          {notice.text}
        </p>
      )}

      <div className="ui-card">
        {loading ? (
          <div className={styles.empty}>
            <span className="ui-spinner" aria-hidden="true" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>No {tab} requests.</p>
        ) : (
          <ul className={styles.list}>
            {rows.map((r) => {
              const busy = busyEmail === r.email;
              const code = codeState(r);
              return (
                <li key={r.email} className={styles.row}>
                  <div className={styles.who}>
                    <span className={styles.email}>{r.email}</span>
                    <span className={styles.meta}>
                      Asked {formatDate(r.requestedAt)}
                      {(r.requestCount ?? 1) > 1 ? ` · ${r.requestCount} times` : ""}
                      {r.decidedAt ? ` · decided ${formatDate(r.decidedAt)}` : ""}
                    </span>
                  </div>
                  {tab === "approved" && <span className={`ui-badge ui-badge--${code.tone}`}>{code.label}</span>}
                  <div className={styles.actions}>
                    {tab === "pending" && (
                      <>
                        <button type="button" className="ui-btn ui-btn--sm" disabled={busy} onClick={() => approve(r.email)}>
                          Approve
                        </button>
                        <button
                          type="button"
                          className="ui-btn ui-btn--sm ui-btn--ghost"
                          disabled={busy}
                          onClick={() => reject(r.email)}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {tab === "approved" && !r.codeUsedAt && (
                      <button
                        type="button"
                        className="ui-btn ui-btn--sm ui-btn--secondary"
                        disabled={busy}
                        onClick={() => resend(r.email)}
                      >
                        Resend code
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
